import { cva, type VariantProps } from 'class-variance-authority';

import type { IconProps } from '../Icon';

export type BulletStyle =
  | 'none'
  | 'bullet'
  | 'check'
  | 'square'
  | 'number'
  | 'arrows'
  | 'q'
  | 'x';

/** Triangle marker direction when `bulletStyle` is `arrows`. */
export type ArrowDirection = 'up' | 'down';

/**
 * Color token for list markers. Icon tokens stay aligned with `iconVariants`.
 * `body` is List-only and maps to the theme body text color (`text-body`).
 * When omitted, each marker keeps its built-in default color.
 */
export type ListMarkerColor = NonNullable<IconProps['color']> | 'body';

/**
 * Text-color class per `ListMarkerColor` token. Text markers (`number`, `q`)
 * use this map. Icon color entries mirror `iconVariants`. `body` is not an
 * Icon color; icon markers inherit it from their wrapper instead.
 */
export const markerColorTextClass: Record<ListMarkerColor, string> = {
  default: 'text-current',
  heading: 'text-heading',
  white: 'text-white',
  primary: 'text-primary-700',
  secondary: 'text-secondary-700',
  black: 'text-black',
  neutral: 'text-neutral-700',
  warning: 'text-warning-900',
  error: 'text-error-500',
  success: 'text-success-500',
  body: 'text-body',
};

/**
 * Native `::marker` color for `bullet` and `square`. Complete class literals
 * so the Tailwind build emits each utility. Do not compose these with a prefix.
 */
export const markerColorMarkerClass: Record<ListMarkerColor, string> = {
  // Arbitrary value: the theme has no `current` color, so `marker:text-current`
  // is not emitted. This literal still sets the marker to the row's text color.
  default: 'marker:text-[currentColor]',
  heading: 'marker:text-heading',
  white: 'marker:text-white',
  primary: 'marker:text-primary-700',
  secondary: 'marker:text-secondary-700',
  black: 'marker:text-black',
  neutral: 'marker:text-neutral-700',
  warning: 'marker:text-warning-900',
  error: 'marker:text-error-500',
  success: 'marker:text-success-500',
  body: 'marker:text-body',
};

const defaultNativeMarkerClass = {
  bullet: 'marker:text-primary-700',
  square: 'marker:text-neutral-500',
} as const;

/**
 * Row content color, a curated subset of `Link` variants so linked rows keep
 * their base/hover/active colors. `default` leaves today's link styling.
 */
export type ListLinkColor =
  | 'default'
  | 'body'
  | 'black'
  | 'subtext'
  | 'primary'
  | 'secondary';

/** Text-color class for non-link row content (plain text / leading icon). */
export const listLinkColorTextClass: Record<ListLinkColor, string> = {
  default: '',
  body: 'text-body',
  black: 'text-black',
  subtext: 'text-subtext',
  primary: 'text-primary-700',
  secondary: 'text-secondary-700',
};

/**
 * One marker color class for native lists. An explicit `markerColor` replaces
 * the style default; both classes are never returned together.
 */
export function nativeListMarkerClass(
  bulletStyle: BulletStyle,
  markerColor?: ListMarkerColor,
): string | undefined {
  if (bulletStyle !== 'bullet' && bulletStyle !== 'square') {
    return undefined;
  }

  if (markerColor) {
    return markerColorMarkerClass[markerColor];
  }

  return defaultNativeMarkerClass[bulletStyle];
}

export const listVariants = cva('flex flex-col', {
  variants: {
    bulletStyle: {
      none: 'list-none',
      bullet: 'list-disc pl-6',
      square: 'list-square pl-6',
      check: 'list-none',
      number: 'list-none',
      arrows: 'list-none',
      q: 'list-none',
      x: 'list-none',
    },
    itemGap: {
      none: 'gap-0',
      xxs: 'gap-xxs',
      xs: 'gap-xs',
      sm: 'gap-sm',
      md: 'gap-md',
      lg: 'gap-lg',
    },
    width: {
      fill: 'w-full',
      hug: 'w-fit max-w-full',
    },
    /**
     * Hairline rule between rows; drops the inter-row gap, including any
     * `itemGap`, so the divider is the only separator. Plain rows use full-bleed `divide-y`; interactive rows
     * draw an inset hairline per row (see `listItemVariants`).
     */
    divided: {
      true: 'gap-0',
      false: '',
    },
    interactive: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    { divided: true, interactive: false, class: 'divide-y divide-neutral-100' },
    { interactive: true, class: 'gap-0' },
  ],
  defaultVariants: {
    bulletStyle: 'check',
    itemGap: 'xs',
    divided: false,
    interactive: false,
  },
});

export const listItemVariants = cva('font-body text-b4 leading-b4', {
  variants: {
    divided: {
      true: '',
      false: '',
    },
    /**
     * Interactive rows delegate their padding to the row element (anchor/span)
     * so the hover tint fills the cell edge-to-edge, so the `<li>` adds none.
     */
    interactive: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    // Plain divided rows get vertical padding so each hairline reads as a
    // full-width row separator with breathing room.
    {
      divided: true,
      interactive: false,
      class: 'py-sm first:pt-0 last:pb-0',
    },
    // Interactive rows own their padding on the row element; the `<li>` carries
    // the full-bleed hover tint so it stays a square rectangle covering the
    // whole cell. `endRadius` rounds the inner anchor (`[&>*]`) for the focus
    // ring only, keeping the tint square — the card's `clipContent` is what
    // rounds the tint's first/last corners to the card radius.
    {
      interactive: true,
      class: 'p-0 transition-colors hover:bg-neutral-100',
    },
    // Interactive divided rows draw the hairline as an inset pseudo-element so
    // it aligns with the row padding instead of spanning edge-to-edge.
    {
      divided: true,
      interactive: true,
      class:
        'relative before:absolute before:inset-x-md before:top-0 before:h-px before:bg-neutral-100 first:before:hidden',
    },
  ],
  defaultVariants: {
    divided: false,
    interactive: false,
  },
});

/**
 * Layout for an interactive row's inner element (anchor/span). The row owns its
 * icon gap and padding (composed from `listIconGapClass` / `listRowPaddingClass`)
 * so the hover tint is full-bleed and both are brand-configurable.
 */
export const interactiveRowBaseClass =
  'flex w-full items-center transition-colors';

/**
 * Keyboard-focus affordance layered onto a linked interactive row. The hover
 * tint lives on the `<li>` (full-bleed, square) so it covers the whole cell;
 * only the `ring-inset` focus ring sits here, so `endRadius` rounding the anchor
 * follows the card corner on the first/last row without rounding the tint.
 */
export const interactiveRowLinkClass =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset focus-visible:ring-offset-0';

/**
 * Size of a row's leading icon. `md` (24px) is the historical default; `sm-md`
 * (20px) matches tighter contact blocks. Passed to `<Icon size>`.
 */
export type ListIconSize = 'sm-md' | 'md';

export const LIST_ICON_SIZE_DEFAULT: ListIconSize = 'md';

/**
 * Gap between a row's leading icon and its label. Distinct from `itemGap`
 * (space *between* rows). Default `sm` (16px); `xxs` (8px) suits tighter rows.
 */
export type ListIconGap = 'xxs' | 'xs' | 'sm' | 'md';

export const listIconGapClass: Record<ListIconGap, string> = {
  xxs: 'gap-xxs',
  xs: 'gap-xs',
  sm: 'gap-sm',
  md: 'gap-md',
};

export const LIST_ICON_GAP_DEFAULT: ListIconGap = 'sm';

/**
 * Padding preset for interactive rows. `comfortable` keeps today's 20px L/R,
 * 16px T/B; `compact` is 16px L/R, 8px T/B. Configurable rather than a
 * default change so it does not re-pad every interactive list.
 */
export type ListRowPadding = 'comfortable' | 'compact';

export const listRowPaddingClass: Record<ListRowPadding, string> = {
  comfortable: 'px-md py-sm',
  compact: 'px-sm py-xxs',
};

export const LIST_ROW_PADDING_DEFAULT: ListRowPadding = 'comfortable';

/**
 * Rounds the first/last interactive row's outer corners to the given radius so
 * their inset focus ring follows the corner instead of being clipped away by a
 * rounded, `clipContent` card. Match it to the card's `rounded` token. `none`
 * (default) leaves rows square. Classes target the row element (`[&>*]`) under
 * the first/last `<li>`.
 */
export type ListEndRadius = 'none' | 'md' | 'lg' | 'xl';

export const listEndRadiusClass: Record<ListEndRadius, string> = {
  none: '',
  md: 'first:[&>*]:rounded-t-md last:[&>*]:rounded-b-md',
  lg: 'first:[&>*]:rounded-t-lg last:[&>*]:rounded-b-lg',
  xl: 'first:[&>*]:rounded-t-xl last:[&>*]:rounded-b-xl',
};

export const LIST_END_RADIUS_DEFAULT: ListEndRadius = 'none';

/**
 * Minimum height for interactive rows so a sparse contact block keeps a
 * comfortable, even tap target. `auto` (default) lets the row size to its
 * padding and content; `md` (48px) suits a seller-contact row, `lg`
 * (56px) a roomier menu. Applied to the row element so the hover tint fills the
 * full height. No effect on non-interactive rows.
 */
export type ListRowHeight = 'auto' | 'md' | 'lg';

export const listRowHeightClass: Record<ListRowHeight, string> = {
  auto: '',
  md: 'min-h-12',
  lg: 'min-h-14',
};

export const LIST_ROW_HEIGHT_DEFAULT: ListRowHeight = 'auto';

export const customMarkerBulletStyles = [
  'check',
  'number',
  'arrows',
  'q',
  'x',
] as const satisfies readonly BulletStyle[];

export function hasCustomMarker(bulletStyle: BulletStyle): boolean {
  return (customMarkerBulletStyles as readonly string[]).includes(bulletStyle);
}

/** Trim CMS/authoring strings; only exact `down` renders red downward triangles. */
export function resolveArrowDirection(
  value: ArrowDirection | string | null | undefined,
): ArrowDirection {
  if (typeof value === 'string' && value.trim() === 'down') {
    return 'down';
  }
  return 'up';
}

export type ListVariantProps = VariantProps<typeof listVariants>;
